import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import RichTextEditor from '../components/RichTextEditor';
import './CreateHealthProgramPage.css';

const CreateHealthProgramPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [programCategories, setProgramCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [faqs, setFaqs] = useState([]);
  const [showJsonImport, setShowJsonImport] = useState(false);
  const [jsonInput, setJsonInput] = useState('');
  const [jsonError, setJsonError] = useState('');

  const JSON_TEMPLATE = JSON.stringify({
    title: 'Community Mental Health Support Program',
    description: 'A program providing mental health resources and support to RSMIN community members.',
    category: 'health',
    programType: ['Counselling'],
    focusAreas: ['Mental Health'],
    targetPopulation: ['All Ages', 'Adults'],
    eligibilityCriteria: 'Must be a recognized RSMIN citizen or family member.',
    fundingAmount: 0,
    fundingType: 'Free Service',
    applicationDeadline: '2026-12-31',
    applicationProcess: 'Complete the online application and submit supporting documents.',
    requiredDocuments: ['Photo ID', 'Proof of RSMIN Citizenship'],
    contactInfo: {
      name: 'Program Coordinator',
      email: 'health@rsmin.ca',
      phone: '(800) 555-0100',
      location: 'RSMIN Community Centre',
      hours: 'Mon–Fri 9AM–5PM'
    },
    isOngoing: true,
    startDate: '',
    endDate: '',
    maxParticipants: '',
    isPublished: false,
    isFeatured: false,
    visibleTo: ['public', 'user', 'citizen'],
    sections: [
      {
        label: 'Eligibility & Background',
        description: 'Help us confirm your eligibility for this program.',
        weight: 50,
        questions: [
          {
            label: 'Are you a recognized RSMIN citizen or immediate family member?',
            type: 'radio',
            required: true,
            weight: 25,
            options: ['Yes', 'No'],
            placeholder: '',
            helpText: ''
          },
          {
            label: 'Please describe your current mental health support needs.',
            type: 'textarea',
            required: true,
            weight: 25,
            options: [],
            placeholder: 'Describe your needs...',
            helpText: 'This information is confidential and used only for program placement.'
          }
        ]
      },
      {
        label: 'Commitments & Consent',
        description: '',
        weight: 50,
        questions: [
          {
            label: 'I consent to participate in this program and understand its terms.',
            type: 'checkbox',
            required: true,
            weight: 50,
            options: [],
            placeholder: '',
            helpText: ''
          }
        ]
      }
    ]
  }, null, 2);

  const handleJsonImport = () => {
    setJsonError('');
    try {
      const parsed = JSON.parse(jsonInput);
      setFormData(prev => ({
        ...prev,
        title: parsed.title || '',
        description: parsed.description || prev.description,
        category: parsed.category || prev.category,
        programType: Array.isArray(parsed.programType) ? parsed.programType : prev.programType,
        focusAreas: Array.isArray(parsed.focusAreas) ? parsed.focusAreas : prev.focusAreas,
        targetPopulation: Array.isArray(parsed.targetPopulation) ? parsed.targetPopulation : prev.targetPopulation,
        eligibilityCriteria: parsed.eligibilityCriteria || prev.eligibilityCriteria,
        fundingAmount: parsed.fundingAmount !== undefined ? parsed.fundingAmount : prev.fundingAmount,
        fundingType: parsed.fundingType || prev.fundingType,
        applicationDeadline: parsed.applicationDeadline ? parsed.applicationDeadline.slice(0, 10) : '',
        applicationProcess: parsed.applicationProcess || prev.applicationProcess,
        requiredDocuments: Array.isArray(parsed.requiredDocuments) ? parsed.requiredDocuments : prev.requiredDocuments,
        contactInfo: parsed.contactInfo ? { ...prev.contactInfo, ...parsed.contactInfo } : prev.contactInfo,
        isOngoing: Boolean(parsed.isOngoing),
        startDate: parsed.startDate ? parsed.startDate.slice(0, 10) : '',
        endDate: parsed.endDate ? parsed.endDate.slice(0, 10) : '',
        maxParticipants: parsed.maxParticipants || '',
        isPublished: Boolean(parsed.isPublished),
        isFeatured: Boolean(parsed.isFeatured),
        visibleTo: Array.isArray(parsed.visibleTo) && parsed.visibleTo.length ? parsed.visibleTo : prev.visibleTo,
        sections: Array.isArray(parsed.sections) ? parsed.sections : prev.sections
      }));
      setShowJsonImport(false);
      setJsonInput('');
    } catch (err) {
      setJsonError('Invalid JSON — please check the format and try again.');
    }
  };

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'health',
    programType: [],
    focusAreas: [],
    targetPopulation: ['All Ages'],
    eligibilityCriteria: '',
    fundingAmount: '',
    fundingType: 'Free Service',
    applicationDeadline: '',
    applicationProcess: '',
    requiredDocuments: [],
    contactInfo: {
      name: '',
      email: '',
      phone: '',
      location: '',
      hours: ''
    },
    sections: [],
    status: 'draft',
    isPublished: false,
    isFeatured: false,
    startDate: '',
    endDate: '',
    isOngoing: false,
    maxParticipants: '',
    visibleTo: ['public', 'user', 'citizen'],
    linkedFaqs: []
  });

  // Fetch program categories from database
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get('/api/program-categories?activeOnly=true');
        setProgramCategories(response.data.categories || []);
        
        // Set default category and program type
        if (response.data.categories && response.data.categories.length > 0) {
          const requestedCategory = searchParams.get('category') || (location.pathname === '/create-grant' ? 'education' : null);
          const defaultCategory = response.data.categories.find(c => c.key === requestedCategory)
            || response.data.categories.find(c => c.key === 'health')
            || response.data.categories[0];
          setFormData(prev => ({
            ...prev,
            category: defaultCategory.key,
            programType: []
          }));
        }
        
        setLoadingCategories(false);
      } catch (err) {
        console.error('Error fetching program categories:', err);
        setError('Failed to load program categories');
        setLoadingCategories(false);
      }
    };
    
    fetchCategories();
  }, [location.pathname, searchParams]);

  useEffect(() => {
    api.get('/api/faq/public?limit=200&sortBy=title&sortOrder=asc')
      .then(res => setFaqs(res.data.posts || []))
      .catch(() => {});
  }, []);

  // Get current category configuration
  const getCurrentCategory = () => {
    return programCategories.find(c => c.key === formData.category) || { programTypes: [], focusAreas: [] };
  };

  // Get dynamic program types and focus areas based on selected category
  const programTypes = getCurrentCategory().programTypes || [];
  const focusAreas = getCurrentCategory().focusAreas || [];
  
  // Get target populations and funding types from category or use defaults
  const defaultTargetPopulations = [
    'All Ages',
    'Children',
    'Youth',
    'Adults',
    'Elders',
    'Families',
    'Women',
    'Men',
    'Specific Conditions'
  ];
  const defaultFundingTypes = [
    'Grant',
    'Subsidy',
    'Free Service',
    'Sliding Scale',
    'Insurance Covered',
    'Scholarship',
    'Bursary',
    'One-time Payment',
    'Recurring Support'
  ];
  
  const targetPopulations = (getCurrentCategory().targetPopulations && getCurrentCategory().targetPopulations.length > 0) 
    ? getCurrentCategory().targetPopulations 
    : defaultTargetPopulations;
    
  const fundingTypes = (getCurrentCategory().fundingTypes && getCurrentCategory().fundingTypes.length > 0)
    ? getCurrentCategory().fundingTypes
    : defaultFundingTypes;

  const handleCategoryChange = (e) => {
    const newCategory = e.target.value;
    const categoryConfig = programCategories.find(c => c.key === newCategory);
    
    setFormData(prev => ({
      ...prev,
      category: newCategory,
      programType: [], // Clear program types when category changes
      focusAreas: [] // Clear focus areas when category changes
    }));
  };

  const handleProgramTypeChange = (e) => {
    const selected = Array.from(e.target.selectedOptions, o => o.value);
    setFormData(prev => ({ ...prev, programType: selected }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const addSection = () => {
    setFormData(prev => ({
      ...prev,
      sections: [
        ...prev.sections,
        {
          label: '',
          description: '',
          weight: 50,
          questions: []
        }
      ]
    }));
  };

  const removeSection = (index) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== index)
    }));
  };

  const handleSectionChange = (index, field, value) => {
    setFormData(prev => {
      const sections = [...prev.sections];
      sections[index][field] = value;
      return { ...prev, sections };
    });
  };

  const addQuestion = (sectionIndex) => {
    setFormData(prev => {
      const sections = [...prev.sections];
      sections[sectionIndex] = {
        ...sections[sectionIndex],
        questions: [
          ...sections[sectionIndex].questions,
          {
            label: '',
            type: 'text',
            weight: 10,
            required: false,
            options: []
          }
        ]
      };
      return { ...prev, sections };
    });
  };

  const removeQuestion = (sectionIndex, questionIndex) => {
    setFormData(prev => {
      const sections = [...prev.sections];
      sections[sectionIndex].questions = sections[sectionIndex].questions.filter((_, i) => i !== questionIndex);
      return { ...prev, sections };
    });
  };

  const handleQuestionChange = (sectionIndex, questionIndex, field, value) => {
    setFormData(prev => {
      const sections = [...prev.sections];
      sections[sectionIndex].questions[questionIndex][field] = value;
      return { ...prev, sections };
    });
  };

  const handleQuestionOptionsChange = (sectionIndex, questionIndex, value) => {
    const options = value.split('\n').filter(opt => opt.trim());
    handleQuestionChange(sectionIndex, questionIndex, 'options', options);
  };

  const handleFocusAreaChange = (area) => {
    setFormData(prev => ({
      ...prev,
      focusAreas: prev.focusAreas.includes(area)
        ? prev.focusAreas.filter(a => a !== area)
        : [...prev.focusAreas, area]
    }));
  };

  const handleTargetPopulationChange = (pop) => {
    setFormData(prev => ({
      ...prev,
      targetPopulation: prev.targetPopulation.includes(pop)
        ? prev.targetPopulation.filter(p => p !== pop)
        : [...prev.targetPopulation, pop]
    }));
  };

  const handleVisibleToChange = (tier) => {
    setFormData(prev => ({
      ...prev,
      visibleTo: prev.visibleTo.includes(tier)
        ? prev.visibleTo.filter(t => t !== tier)
        : [...prev.visibleTo, tier]
    }));
  };

  const handleArrayFieldChange = (field, value) => {
    const items = value.split('\n').filter(item => item.trim());
    setFormData(prev => ({
      ...prev,
      [field]: items
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await api.post('/api/health-programs', formData);
      navigate('/staff-health-programs');
    } catch (err) {
      setError(err.response?.data?.message || 'Error creating health program');
      setLoading(false);
    }
  };

  return (
    <div className="create-health-program-container">
      <div className="page-header">
        <h1>Create New Program</h1>
        <button onClick={() => navigate('/staff-health-programs')} className="btn-back">
          ← Back to Programs
        </button>
      </div>

      {loadingCategories ? (
        <div className="loading-message">Loading program categories...</div>
      ) : error && programCategories.length === 0 ? (
        <div className="error-message">{error}</div>
      ) : null}

      {error && programCategories.length > 0 && <div className="error-message">{error}</div>}

      {/* JSON Import Panel */}
      {!loadingCategories && programCategories.length > 0 && (
        <>
          <div className="json-import-bar">
            <button
              type="button"
              className="btn-json-toggle"
              onClick={() => { setShowJsonImport(v => !v); setJsonError(''); }}
            >
              {showJsonImport ? '✕ Close JSON Import' : '{ } Import from JSON'}
            </button>
            {!showJsonImport && (
              <span className="json-bar-hint">Paste a program JSON structure to auto-fill the form below.</span>
            )}
          </div>

          {showJsonImport && (
            <div className="json-import-panel">
              <div className="json-import-header">
                <h3>📋 Import Program from JSON</h3>
                <p>The template below shows the full structure. Copy it, edit the values, paste it back, then click <strong>Load into Form</strong>.</p>
              </div>
              <div className="json-template-block">
                <div className="json-template-label">
                  <span>Example Template</span>
                  <button
                    type="button"
                    className="btn-copy-json"
                    onClick={() => navigator.clipboard.writeText(JSON_TEMPLATE)}
                  >
                    📋 Copy Template
                  </button>
                </div>
                <pre className="json-template-pre">{JSON_TEMPLATE}</pre>
              </div>
              <div className="json-paste-block">
                <label htmlFor="prog-json-paste">Paste your edited JSON here:</label>
                <textarea
                  id="prog-json-paste"
                  className="json-paste-area"
                  rows={14}
                  value={jsonInput}
                  onChange={e => { setJsonInput(e.target.value); setJsonError(''); }}
                  placeholder="Paste your JSON here..."
                  spellCheck={false}
                />
              </div>
              {jsonError && <div className="json-error">{jsonError}</div>}
              <div className="json-import-actions">
                <button type="button" className="btn btn-primary" onClick={handleJsonImport} disabled={!jsonInput.trim()}>
                  ⬇ Load into Form
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => { setShowJsonImport(false); setJsonInput(''); setJsonError(''); }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {!loadingCategories && programCategories.length > 0 && (
      <form onSubmit={handleSubmit} className="program-form">
        <div className="form-section">
          <h2>Basic Information</h2>
          
          <div className="form-group">
            <label>Program Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              placeholder="e.g., Community Mental Health Support"
            />
          </div>

          <div className="form-group">
            <label>Description *</label>
            <RichTextEditor
              value={formData.description}
              onChange={(val) => setFormData(prev => ({ ...prev, description: val }))}
              placeholder="Provide a detailed description of the health program..."
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Program Category *</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleCategoryChange}
                required
              >
                {programCategories.map(category => (
                  <option key={category.key} value={category.key}>
                    {category.icon} {category.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Program Type *</label>
              <div className="checkbox-group">
                {programTypes.map(type => (
                  <label key={type} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={(formData.programType || []).includes(type)}
                      onChange={() => {
                        setFormData(prev => ({
                          ...prev,
                          programType: (prev.programType || []).includes(type)
                            ? prev.programType.filter(t => t !== type)
                            : [...(prev.programType || []), type]
                        }));
                      }}
                    />
                    {type}
                  </label>
                ))}
              </div>
            </div>

          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Target Population *</label>
              <div className="checkbox-group">
                {targetPopulations.map(pop => (
                  <label key={pop} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.targetPopulation.includes(pop)}
                      onChange={() => handleTargetPopulationChange(pop)}
                    />
                    {pop}
                  </label>
                ))}
              </div>
            </div>

            {focusAreas.length > 0 && (
              <div className="form-group">
                <label>Focus Areas ({getCurrentCategory().label})</label>
                <div className="checkbox-group">
                  {focusAreas.map(area => (
                    <label key={area} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.focusAreas.includes(area)}
                        onChange={() => handleFocusAreaChange(area)}
                      />
                      {area}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="form-section">
          <h2>Eligibility & Application</h2>
          
          <div className="form-group">
            <label>Eligibility Criteria *</label>
            <RichTextEditor
              value={formData.eligibilityCriteria}
              onChange={(val) => setFormData(prev => ({ ...prev, eligibilityCriteria: val }))}
              placeholder="Who is eligible for this program?"
            />
          </div>

          <div className="form-group">
            <label>Application Process *</label>
            <RichTextEditor
              value={formData.applicationProcess}
              onChange={(val) => setFormData(prev => ({ ...prev, applicationProcess: val }))}
              placeholder="Describe how to apply for this program..."
            />
          </div>

          <div className="form-group">
            <label>Required Documents (one per line)</label>
            <textarea
              value={formData.requiredDocuments.join('\n')}
              onChange={(e) => handleArrayFieldChange('requiredDocuments', e.target.value)}
              rows="3"
              placeholder="Photo ID&#10;Proof of Residence&#10;Medical Records"
            />
          </div>

          {faqs.length > 0 && (
            <div className="form-group">
              <label>Linked FAQs</label>
              <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: 6 }}>Hold Ctrl / Cmd to select multiple. Selected FAQs will appear as links on this program's detail page.</p>
              <select
                multiple
                value={formData.linkedFaqs || []}
                onChange={e => setFormData(prev => ({
                  ...prev,
                  linkedFaqs: Array.from(e.target.selectedOptions, o => o.value)
                }))}
                style={{ width: '100%', minHeight: 120 }}
              >
                {faqs.map(faq => (
                  <option key={faq._id} value={faq._id}>{faq.title}</option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label>Application Deadline</label>
            <input
              type="date"
              name="applicationDeadline"
              value={formData.applicationDeadline}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-section">
          <h2>Funding Information</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label>Funding Type</label>
              <select
                name="fundingType"
                value={formData.fundingType}
                onChange={handleChange}
              >
                {fundingTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Funding Amount ($)</label>
              <input
                type="number"
                name="fundingAmount"
                value={formData.fundingAmount}
                onChange={handleChange}
                min="0"
                placeholder="0"
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>Contact Information</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label>Contact Name</label>
              <input
                type="text"
                name="contactInfo.name"
                value={formData.contactInfo.name}
                onChange={handleChange}
                placeholder="John Doe"
              />
            </div>

            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="contactInfo.email"
                value={formData.contactInfo.email}
                onChange={handleChange}
                placeholder="contact@example.com"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Phone</label>
              <input
                type="tel"
                name="contactInfo.phone"
                value={formData.contactInfo.phone}
                onChange={handleChange}
                placeholder="(123) 456-7890"
              />
            </div>

            <div className="form-group">
              <label>Location</label>
              <input
                type="text"
                name="contactInfo.location"
                value={formData.contactInfo.location}
                onChange={handleChange}
                placeholder="123 Main St, City, State"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Hours of Operation</label>
            <input
              type="text"
              name="contactInfo.hours"
              value={formData.contactInfo.hours}
              onChange={handleChange}
              placeholder="Mon-Fri 9AM-5PM"
            />
          </div>
        </div>

        <div className="form-section">
          <h2>Program Schedule</h2>
          
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="isOngoing"
                checked={formData.isOngoing}
                onChange={handleChange}
              />
              This is an ongoing program
            </label>
          </div>

          {!formData.isOngoing && (
            <div className="form-row">
              <div className="form-group">
                <label>Start Date</label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>End Date</label>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                />
              </div>
            </div>
          )}

          <div className="form-group">
            <label>Maximum Participants</label>
            <input
              type="number"
              name="maxParticipants"
              value={formData.maxParticipants}
              onChange={handleChange}
              min="0"
              placeholder="Leave blank for unlimited"
            />
          </div>
        </div>

        {/* Application Form Builder */}
        <div className="form-section">
          <div className="section-header">
            <h2>Application Form Sections</h2>
            <button type="button" onClick={addSection} className="btn-add-section">
              + Add Section
            </button>
          </div>
          
          {formData.sections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="section-builder">
              <div className="section-builder-header">
                <h3>Section {sectionIndex + 1}</h3>
                <button
                  type="button"
                  onClick={() => removeSection(sectionIndex)}
                  className="btn-remove"
                >
                  Remove Section
                </button>
              </div>

              <div className="form-group">
                <label>Section Title *</label>
                <input
                  type="text"
                  value={section.label}
                  onChange={(e) => handleSectionChange(sectionIndex, 'label', e.target.value)}
                  placeholder="e.g., Personal Information"
                  required
                />
              </div>

              <div className="form-group">
                <label>Section Description</label>
                <textarea
                  value={section.description}
                  onChange={(e) => handleSectionChange(sectionIndex, 'description', e.target.value)}
                  placeholder="Brief description of this section"
                  rows="2"
                />
              </div>

              <div className="form-group">
                <label>Section Weight (0-100) *</label>
                <input
                  type="number"
                  value={section.weight}
                  onChange={(e) => handleSectionChange(sectionIndex, 'weight', parseInt(e.target.value) || 0)}
                  min="0"
                  max="100"
                  required
                />
              </div>

              <div className="questions-section">
                <div className="questions-header">
                  <h4>Questions</h4>
                  <button
                    type="button"
                    onClick={() => addQuestion(sectionIndex)}
                    className="btn-add-question"
                  >
                    + Add Question
                  </button>
                </div>

                {section.questions.map((question, questionIndex) => (
                  <div key={questionIndex} className="question-builder">
                    <div className="question-builder-header">
                      <span>Question {questionIndex + 1}</span>
                      <button
                        type="button"
                        onClick={() => removeQuestion(sectionIndex, questionIndex)}
                        className="btn-remove-small"
                      >
                        Remove
                      </button>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Question Text *</label>
                        <input
                          type="text"
                          value={question.label}
                          onChange={(e) => handleQuestionChange(sectionIndex, questionIndex, 'label', e.target.value)}
                          placeholder="Enter your question"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Question Type *</label>
                        <select
                          value={question.type}
                          onChange={(e) => handleQuestionChange(sectionIndex, questionIndex, 'type', e.target.value)}
                          required
                        >
                          <option value="text">Text</option>
                          <option value="textarea">Long Text</option>
                          <option value="checkbox">Checkbox</option>
                          <option value="dropdown">Dropdown</option>
                          <option value="multi-select">Multi-Select</option>
                          <option value="file">File Upload</option>
                          <option value="date">Date</option>
                          <option value="priority">Priority</option>
                        </select>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Weight (0-100) *</label>
                        <input
                          type="number"
                          value={question.weight}
                          onChange={(e) => handleQuestionChange(sectionIndex, questionIndex, 'weight', parseInt(e.target.value) || 0)}
                          min="0"
                          max="100"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={question.required}
                            onChange={(e) => handleQuestionChange(sectionIndex, questionIndex, 'required', e.target.checked)}
                          />
                          Required Question
                        </label>
                      </div>
                    </div>

                    {(question.type === 'dropdown' || question.type === 'multi-select') && (
                      <div className="form-group">
                        <label>Options (one per line) *</label>
                        <textarea
                          value={question.options.join('\n')}
                          onChange={(e) => handleQuestionOptionsChange(sectionIndex, questionIndex, e.target.value)}
                          placeholder="Option 1&#10;Option 2&#10;Option 3"
                          rows="4"
                          required
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="form-section">
          <h2>Publishing Options</h2>
          
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="isPublished"
                checked={formData.isPublished}
                onChange={handleChange}
              />
              Publish immediately
            </label>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="isFeatured"
                checked={formData.isFeatured}
                onChange={handleChange}
              />
              Feature this program
            </label>
          </div>

          <div className="form-group">
            <label>Visible To (Access Levels)</label>
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.visibleTo.includes('public')}
                  onChange={() => handleVisibleToChange('public')}
                />
                🌍 Public (Not logged in visitors)
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.visibleTo.includes('user')}
                  onChange={() => handleVisibleToChange('user')}
                />
                👤 User (Logged in, not verified)
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.visibleTo.includes('citizen')}
                  onChange={() => handleVisibleToChange('citizen')}
                />
                ✓ Citizen (Verified with card number)
              </label>
            </div>
            <small style={{ color: '#666', marginTop: '0.5rem', display: 'block' }}>
              Select which user tiers can view this program. Leave all checked for public access.
            </small>
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            onClick={() => navigate('/staff-health-programs')}
            className="btn-cancel"
          >
            Cancel
          </button>
          <button 
            type="submit" 
            disabled={loading}
            className="btn-submit"
          >
            {loading ? 'Creating...' : 'Create Program'}
          </button>
        </div>
      </form>
      )}
    </div>
  );
};

export default CreateHealthProgramPage;
